/* PDP behaviors — ported 1:1 from pdp-client.tsx (gallery, tabs, accordion,
   running total). Add-to-cart + the quantity stepper mechanics live in cart.js.
   No-ops when the PDP isn't on the page. */
(function () {
  "use strict";

  /* ══ PDP HERO GALLERY — arrows + swipe on the main image ══════════════════
     Markup: sections/main-product.liquid (.pdp-gallery). Behaviour: this
     block. CSS: src/screens.css, the .pdp-* group after :367.

     THE SRC-SWAP IS GONE. What used to live here did
     `main.removeAttribute("srcset"); main.src = …` on one <img>, which knocked
     the hero permanently off the responsive ladder on the first thumbnail
     click and gave a drag zero follow-the-finger feedback. There is no single
     hero <img> to swap any more: the hero is N pre-rendered slides and the
     index is a transform. The ONLY srcset write in this file is hydrate()
     below, which ADDS one. `.src =` appears nowhere. Keep it that way.

     ── TRACK CONTRACT v1 ────────────────────────────────────────────────────
     The same seven invariants the product-card gallery runs on (assets/home.js
     top block; snippets/product-card.liquid; components.css:236-374):
       1. JS owns the INDEX and nothing else. It writes --i inline; CSS turns
          that into a transitioned translateX. No state class.
       2. The index is READ FROM THE DOM (shown()), never remembered.
       3. The gesture listens on the STAGE, not the track — the arrows are
          painted over the shot as siblings and would otherwise dead-zone 88px
          of a 350px phone box (25%; 31% at a 320 viewport, measured) at
          exactly the height a swipe starts.
       4. Swipe is TOUCH + PEN ONLY. A mouse drag would write an inline --i on
          a twitch, and desktop already has hover-revealed arrows.
       5. Live 1:1 follow by writing a FRACTIONAL --i; every read happens at
          the axis LOCK (8px, 1.2 vertical bias), never at pointerdown, so a
          tap does zero layout and zero style reads.
       6. visible(lw) reads the RENDERED transform matrix, so a drag begun
          inside a running transition follows the pixels, not the destination.
       7. Percentage transform => a zero-width element needs no re-measure.
     PROHIBITION, worth stating because it looks like an optimisation:
     --i MUST STAY AN UNREGISTERED CUSTOM PROPERTY. Registering it via
     @property makes it INTERPOLATE, which silently breaks visible()'s entire
     premise (it exists because --i flips to the destination on frame 1 while
     the transform glides) and breaks jump()'s snap.
     CHANGES TO AN INVARIANT MUST LAND IN BOTH FILES.
     CHANGES TO POLICY ARE PER-SURFACE AND MUST NOT BE PORTED.

     ── THIS IS A DELIBERATE SIBLING, NOT A STALE FORK ───────────────────────
     No shared module, and the reason is blast radius, not taste: home.js runs
     on EVERY template (theme.liquid:92) and this file only on product (:93).
     Sharing means every future PDP-gallery fix edits a file that executes on
     every page of a live store with no tests and no JS build step. The reverse
     coupling does not exist. What actually repeats is ~90 lines of pointer
     PHYSICS with near-zero change pressure; what differs is everything under
     product pressure:
       card: 2 slides by policy   | PDP: unbounded by policy
       card: CSS hover peek       | PDP: NO peek -> release(), `paged` and the
                                         d.inline snapshot are DELETED, not
                                         ported. That is the peek's only cost
                                         on this surface, refunded.
       card: no position feedback | PDP: a thumb strip that must stay in sync
             (2 slides = a toggle)      and scroll-follow, from ONE writer
       card: all slides eager     | PDP: a hydration window
       card: excludes quick-add   | PDP: nothing to exclude
       card: n=2 so a wrap is     | PDP: needs jump()'s |delta|>1 SNAP, which
             |delta|=1 and the          the card can never need and which a
             straight transition        straight port of go() gets WRONG (a
             is correct                 420ms strobe past 6 slides)
       card: follow band [0,n-1]  | PDP: band is base+/-1 — IDENTICAL at n=2,
             is the same thing          which is why the card never exposed it
     Names, and every physics constant (8px, 1.2, 30ms, 120ms, 0.5px/ms,
     18%/72px, 700ms), are BYTE-IDENTICAL to home.js on purpose: a side-by-side
     diff of the two blocks must show only the deltas above.
     REVISIT the extract-an-engine question when a THIRD surface needs a track,
     or the first time a physics bug has to be fixed in both files. Not before.
     (One such bug is already known and is NOT fixed here — see the pointerup
     comment about home.js:280.)

     ARIA road not taken: a picker where exactly one child is on is really a
     radiogroup, and "hero selected by a strip" is really a tablist+tabpanel.
     Both rejected as churn for semantics that are a refinement, not a fix;
     aria-current already says the true thing. */
  var pdpGalleries = document.querySelectorAll("[data-pdp-gallery]");

  /* CLICK SUPPRESSION FOR A COMPLETED DRAG — on `document`, mirroring
     home.js:38-59, NOT stage-scoped. The stage cannot be the scope: the thumb
     strip is a SIBLING of the stage, 16px below a 438px phone hero, and a
     stray click on a thumbnail calls jump(k), silently overwriting the drag's
     commit with a different image. Relying instead on setPointerCapture
     retargeting the compatibility click to the stage is exactly the assumption
     home.js deliberately refused to lean on.
     Capture + stopPropagation, not preventDefault alone, because the arrows
     are <button>s and cart.js:132, cart.js:159 and theme.js:143 are all
     `document` BUBBLE, so one capture listener beats them at any defer order.
     Disarmed by the click it eats, by the next pointerdown, and by a 700ms
     expiry that bounds the one click with no pointerdown in front of it.
     CHECKED against home.js's identical pair, which also runs on this page
     (related cards render here): it registers FIRST and returns immediately
     unless a CARD drag armed it, and stopPropagation on `document` in the
     capture phase does not stop other listeners on `document` itself. The two
     flags live in different closures and are armed by disjoint gestures. No
     collision in either direction. */
  var pdpDragUntil = 0;
  if (pdpGalleries.length) {
    document.addEventListener("click", function (e) {
      if (!pdpDragUntil || Date.now() > pdpDragUntil) return;
      pdpDragUntil = 0;
      e.preventDefault();
      e.stopPropagation();
    }, true);
    document.addEventListener("pointerdown", function () { pdpDragUntil = 0; }, true);
  }

  pdpGalleries.forEach(function (root) {
    var stage  = root.querySelector("[data-pdp-stage]");
    var track  = stage && stage.querySelector("[data-pdp-track]");
    var prev   = stage && stage.querySelector("[data-pdp-prev]");
    var next   = stage && stage.querySelector("[data-pdp-next]");
    if (!stage || !track || !prev || !next) return;   // 0 or 1 image
    var slides = track.children;
    var n = slides.length;
    if (n < 2) return;
    var live   = stage.querySelector("[data-pdp-live]");
    var strip  = root.querySelector("[data-pdp-thumbs]");
    var thumbs = strip ? Array.prototype.slice.call(strip.querySelectorAll("[data-pdp-thumb]")) : [];
    var reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    var drag = null;

    /* ── Reads. Invariant 2: the DOM is the source of truth ────────────────
       No `cur` variable exists in this scope. Unlike the card there is no CSS
       peek writing --i behind our back, so a remembered index would in fact be
       correct here — this read is DEFENSIVE, not load-bearing. Kept anyway so
       the two implementations share one mental model and so a future
       CSS-driven state cannot silently desync it. Costs one style read per
       discrete interaction. Unregistered custom property: "" -> NaN -> 0. */
    function shown() {
      var v = parseInt(window.getComputedStyle(track).getPropertyValue("--i"), 10);
      return v > 0 ? v : 0;
    }
    /* Where the PIXELS are, in slide-widths — asked only by the drag. --i is
       unregistered so it is not interpolated: go() flips it to the destination
       on frame 1 while the transform glides there for 240-420ms. A drag begun
       inside that window would otherwise follow from the DESTINATION and jump a
       full slide on its first frame. lw is the LAYOUT width — a % translateX
       resolves against the border box, which is the unit computed transform
       reports px in. Deliberately not the rect width the finger delta uses. */
    function visible(lw) {
      var t = window.getComputedStyle(track).transform;
      if (t.indexOf("matrix") !== 0) return shown();
      var p = t.slice(t.indexOf("(") + 1).split(",");
      var v = -parseFloat(t.indexOf("matrix3d") === 0 ? p[12] : p[4]) / lw;
      return isFinite(v) ? v : shown();
    }

    /* ── Hydration. ONE-WAY, IDEMPOTENT, ADD-ONLY, EXACTLY ONE REQUEST ─────
       Every slide but the first ships a real ~1-2KB 100w src (eager, already
       in cache) plus data-srcset. This swaps in the full ladder.

       THE TWO-STATEMENT BODY IS THE POINT, AND THE ORDER WAS MEASURED. An
       earlier draft flipped loading="lazy" -> "eager" first and then set
       srcset; in a real Chrome that issued TWO requests — the lazy-load
       resumption steps fire immediately against the URL selected at parse time
       (the placeholder), and the srcset write is then a fresh source-set
       update on top. Verified in the network entries. With eager placeholders
       there is no `loading` to flip at all and setting srcset alone produced
       exactly one request. Do not reintroduce a `loading` write here.

       Because the srcset carries w descriptors, the `src` is NOT a candidate
       (verified: currentSrc is always a srcset URL), so the placeholder is
       never re-fetched and never re-selected.

       NOTE WHAT THIS DOES NOT DO: it never assigns .src and never removes a
       srcset. That is the structural fix for the defect at old line 15.
       Removing data-srcset makes it idempotent (three calls -> one request,
       verified) and leaves no URL in the DOM that could later be assigned to
       .src.

       THE BLUR IS CLEARED ON `load`, NOT HERE. It exists to mask a 6.1x
       upscale of a 100px rendition; clearing it when the request FIRES would
       show raw block pixellation on the largest element on the page for the
       whole fetch. `error` too, or a failed fetch leaves a slide blurred
       forever. */
    function hydrate(i) {
      var s = slides[i];
      if (!s || s.tagName !== "IMG") return;
      var ss = s.getAttribute("data-srcset");
      if (!ss) return;
      s.removeAttribute("data-srcset");
      function clear() { s.removeAttribute("data-lqip"); }
      s.addEventListener("load", clear, { once: true });
      s.addEventListener("error", clear, { once: true });
      s.setAttribute("srcset", ss);
    }
    /* THE WINDOW INVARIANT: i and both MODULAR neighbours are hydrated. That
       set is provably the complete set of paintable slides:
         (a) the live drag's fractional --i is BANDED to base +/- 1 (see
             pointermove) and its commit is base +/- 1 clamped, so it can
             reveal at most one neighbour;
         (b) jump() SNAPS any |delta| > 1, so a strip jump or an arrow WRAP
             never paints an intermediate slide at all.
       Modular, not clamped: at index 0 this pre-hydrates n-1, which is where
       "previous" wraps to. Non-adjacent thumbnail jumps are covered separately
       by the per-thumb prefetch below.
       NO idle "hydrate everything" pass, deliberately: on an 8-image product
       that is ~6 full-size renditions a shopper may never look at. The window
       is the correctness guarantee; the eager placeholder makes the residual
       case a blur-up rather than a bug. */
    function warm(i) {
      hydrate(i);
      hydrate((i + 1) % n);
      hydrate((i - 1 + n) % n);
    }

    /* ── The single writer of visible state ────────────────────────────────
       aria-hidden, aria-current and the strip scroll are set HERE and nowhere
       else, and paint() is called only from go(). One source of truth, one
       writer — that is the entire fix for "the strip only updated on click".
       Deliberately NOT called mid-drag: these are accessibility states, not
       animation, and toggling them at pointer rate spams AT. Once, at commit.

       EXACTLY ONE SLIDE IS EXPOSED TO THE A11Y TREE. overflow:hidden +
       translateX removes nothing from it, and unlike the card there is no
       wrapping aria-label'd <a> here to swallow the off-screen alts
       (product-card.liquid:167 relies on exactly that). Without this loop, a
       merchant who correctly fills in alt text for all N images makes the page
       announce N image descriptions in a row, above the <h1>, for a region
       where one image is visible — and "Image 3 of 6" then names content the
       reader cannot reach in place. Slides with alt="" are already
       presentational, so this is free at today's catalogue and correct at N.
       Seeding it from Liquid is not an option: image_tag cannot emit a
       hyphenated parameter, the same constraint that forces data-srcset.

       Scroll-follow is mandatory once the strip overflows (MEASURED: N >= 4 on
       a 350px phone column, N >= 7 on the 599.58px desktop column): an arrow
       press would otherwise move the hero to a shot whose thumbnail is off
       screen and the strip would look frozen on a stale selection. Rect-based,
       so it has no dependency on offsetParent or on the strip's padding.
       Manual scroll, NOT scrollIntoView(): scrollIntoView walks every
       scrollable ancestor and will scroll the PAGE vertically to satisfy
       `block`, which on a PDP yanks the buy box out of view on an arrow press.
       The scrollWidth guard makes the whole thing a no-op at today's count of
       2. `behavior` is chosen in JS because theme.css:512's reduced-motion
       nuke overrides the CSS scroll-behavior property, not this argument. */
    function paint(i) {
      for (var s = 0; s < n; s++) {
        if (s === i) slides[s].removeAttribute("aria-hidden");
        else slides[s].setAttribute("aria-hidden", "true");
      }
      for (var k = 0; k < thumbs.length; k++) {
        thumbs[k].setAttribute("aria-current", k === i ? "true" : "false");
      }
      var b = thumbs[i];
      if (!strip || !b || strip.scrollWidth <= strip.clientWidth + 1) return;
      var sr = strip.getBoundingClientRect(), br = b.getBoundingClientRect(), d = 0;
      if (br.left < sr.left + 12) d = br.left - sr.left - 12;
      else if (br.right > sr.right - 12) d = br.right - sr.right + 12;
      if (!d) return;
      if (strip.scrollBy) strip.scrollBy({ left: d, behavior: reduce ? "auto" : "smooth" });
      else strip.scrollLeft += d;
    }

    /* THE ONLY WRITER OF A COMMITTED INDEX. Everything routes through here.
       Wraps, like the card's arrows: at n === 2 — today's entire catalogue —
       wrap is what makes ONE button a working toggle, which is exactly the
       owner's complaint. `quiet` has ONE caller, settle(), for a drag that
       moved nothing; every discrete control announces, because aria-current is
       not reliably re-announced when it changes on the focused element. */
    function go(i, quiet) {
      var to = ((i % n) + n) % n;
      track.style.setProperty("--i", to);
      warm(to);
      paint(to);
      if (live && !quiet) live.textContent = "Image " + (to + 1) + " of " + n;
    }
    /* SNAP ANY NON-ADJACENT MOVE. THIS IS THE ONE PLACE A STRAIGHT PORT OF THE
       CARD'S go() IS WRONG. A transitioned translateX from 0 to -700% PAINTS
       slides 1..6 flying past in 420ms — a strobe. The card never hits it
       because n === 2 there, so every move including a wrap is |delta| = 1.
       Here a wrap at n = 8 is |delta| = 7 and a thumb click is arbitrary.
       Zeroing the duration and forcing a reflow in the same task commits the
       0s style and the new --i together, so no transition starts; the duration
       is then released for the next move, which cannot re-trigger anything
       because the transform is already at its new value. VERIFIED in a browser
       on this geometry: 1 -> 7 lands the transform at matrix(…,-4197.08,0)
       (= 7 x 599.583) on the synchronous read and 100ms later, with
       transitionDuration back to 0.42s; the adjacent 0 -> 1 move keeps its
       glide. This is also what reduces the hydration obligation to a
       three-slide window.
       The explicit offsetWidth read is NOT redundant: paint() can return
       before touching layout when the strip does not overflow. */
    function jump(i) {
      var to = ((i % n) + n) % n;
      if (Math.abs(to - shown()) > 1) {
        track.style.transitionDuration = "0s";
        go(to);
        void track.offsetWidth;
        track.style.removeProperty("transition-duration");
      } else {
        go(to);
      }
    }
    /* The lock guard is invariant 2 again: mid-drag --i is fractional and
       parseInt("1.42") is 1. Guarding step() covers arrow clicks AND arrow
       keydowns from one place. `drag.lock`, not `drag` — an unlocked drag has
       written nothing and must never disable the arrows for the session. */
    function step(d) { if (drag && drag.lock) return; jump(shown() + d); }

    function onKey(e) {
      if (e.key === "ArrowLeft") { e.preventDefault(); step(-1); }
      else if (e.key === "ArrowRight") { e.preventDefault(); step(1); }
    }
    prev.addEventListener("click", function () { step(-1); });
    next.addEventListener("click", function () { step(1); });
    prev.addEventListener("keydown", onKey);
    next.addEventListener("keydown", onKey);

    /* Thumbnails. A thumb is the ONLY control that can jump more than one
       slide, so it is the only path whose destination might be outside the
       warm window. All three prefetch hooks fire strictly before the click:
       pointerenter gives a desktop pointer hundreds of ms, pointerdown gives a
       finger the tap duration, focus gives a keyboard user however long it
       takes to press Enter. The handler does NOT touch aria-current — it waits
       for paint() to be called back, which is what keeps one writer. */
    thumbs.forEach(function (b, k) {
      function pre() { hydrate(k); }
      b.addEventListener("pointerenter", pre);
      b.addEventListener("pointerdown", pre);
      b.addEventListener("focus", pre);
      b.addEventListener("click", function () { jump(k); });
    });

    /* ── SWIPE — the finger moves the SAME --i the arrows move ─────────────
       translateX(calc(var(--i,0) * -100%)) already reads 1.42 as "42% of the
       way to shot 3", so live-follow is this block's existing variable written
       at pointer rate: one model, one writer at a time, still no state class.
       TOUCH AND PEN ONLY. The commit base is shown(), read from the DOM, never
       remembered — and read at the LOCK, so a plain TAP does zero layout. NO
       rAF; after the lock the handler only WRITES, so recalc defers a frame
       and multiple writes per frame collapse.
       NO d.inline SNAPSHOT, unlike home.js: there is no CSS peek to hand the
       index back to, so an abort simply returns to the integer base through
       the one writer — SILENTLY. `i === d.base` covers both an abort and a
       below-threshold drag that snapped back to where it started; without it,
       every 30px wobble announces a position change that did not happen.
       home.js gets this for free by never calling go() on that path. */
    function settle(d, i) {
      track.style.removeProperty("transition-duration");
      track.style.removeProperty("will-change");
      go(i === null ? d.base : i, i === null || i === d.base);
    }

    stage.addEventListener("pointerdown", function (e) {
      // `drag.lock`, not `drag`: a second finger must not re-enter a LIVE
      // gesture, but an unlocked drag has written nothing, so replacing it is
      // safe — and it self-heals the one strand capture cannot cover, a pen
      // leaving the box inside the first 8px. isPrimary drops a pinch's second
      // finger. NO exclusion list: there is no quick-add here, and the ARROWS
      // are deliberately not excluded — under 8px their click fires untouched,
      // and over 8px a drag begun on "next" and travelling left commits +1,
      // which is the operation that arrow performs.
      if (drag && drag.lock) return;
      if (!e.isPrimary || e.pointerType === "mouse") return;
      drag = { id: e.pointerId, x: e.clientX, y: e.clientY, lock: false, t0: e.timeStamp };
    });

    // No {passive:true}: pointermove is not a scroll-blocking event type,
    // touch-action governs the compositor, and nothing here preventDefaults.
    stage.addEventListener("pointermove", function (e) {
      var d = drag;
      if (!d || e.pointerId !== d.id) return;
      var dx = e.clientX - d.x, dy = e.clientY - d.y;

      if (!d.lock) {
        // UNDECIDED IS NOT REJECTED — never latch an abort here. A terminal
        // verdict on one 8px sample runs a ratio test inside the sensor's
        // noise floor: a 7px finger-roll at touchdown would kill a clean 120px
        // swipe with no second chance. Deferring is free — nothing is written,
        // the page still scrolls under pan-y, and if the browser commits to
        // that scroll it fires pointercancel, the authoritative abort. 1.2
        // bias: ambiguous means vertical, because scrolling past is common.
        if (Math.abs(dx) < 8 || Math.abs(dx) <= Math.abs(dy) * 1.2) return;
        // Rect for the FINGER divisor (it carries any transform scale, so the
        // shot tracks 1:1); offsetWidth for visible(). Both guard w === 0 ->
        // Infinity -> "--i:NaN", which invalidates the calc() and drops the
        // transform entirely.
        var w = track.getBoundingClientRect().width, lw = track.offsetWidth;
        if (!w || !lw) { drag = null; return; }
        d.w = w;
        d.base = shown();                 // commit origin, integer
        d.vis  = visible(lw);             // render origin, fractional
        d.lx = e.clientX;                 // follow measured from HERE, so the
        d.px = e.clientX;                 // 8px slop is not double-counted
        d.pt = e.timeStamp;
        d.v = 0;
        d.lock = true;
        warm(d.base);                     // after the reads, never before
        // Order is load-bearing: visible() is read BEFORE the duration is
        // zeroed and --i is written AFTER, all in one task, so zeroing cancels
        // the running transition and no frame is painted at the old target.
        track.style.transitionDuration = "0s";
        track.style.willChange = "transform";   // only between lock and settle
        // Capture at the LOCK, never at pointerdown. Touch gets implicit
        // capture free; pen does not, so a pen leaving the box would stop
        // delivering moves and never deliver up, parking a fractional --i with
        // its transition suppressed. Capturing at pointerdown would claim every
        // TAP. setPointerCapture throws NotFoundError if the finger lifted
        // between the browser queueing this move and us handling it, and it is
        // last so a throw cannot skip the style setup above.
        try { stage.setPointerCapture(e.pointerId); } catch (err) { /* already released */ }
      }

      // >=30ms (~2 frame) window: sampling every move yields ~0 at pointerup,
      // because the up lands on the last move's coordinates.
      var dt = e.timeStamp - d.pt;
      if (dt >= 30) { d.v = (e.clientX - d.px) / dt; d.px = e.clientX; d.pt = e.timeStamp; }

      // Higher --i = further left = later shot. THE FOLLOW MUST NOT PROMISE
      // MORE THAN THE COMMIT CAN DELIVER. The commit is base +/- 1 clamped, so
      // the band is anchored THERE and not at [0, n-1]. At n === 2 — the
      // card's whole world — base+/-1 clamped IS [0, n-1], so this is
      // byte-equivalent to home.js:269-271 there; it only differs from n >= 3,
      // which only exists on this surface. That is why the card never exposed
      // it: banding at [0, n-1] on an 8-image product lets one long haul write
      // --i:3.4, painting slides the warm window never hydrated and then
      // rubber-banding TWO slides backwards on release — "drag three, get one",
      // the single most common complaint about hand-rolled carousels.
      // It is also what makes warm()'s +/-1 window PROVABLY sufficient:
      // past the band the surplus is divided by 3, so exposing a full
      // unhydrated slide would need three extra slide-widths of travel
      // (1050px on a 350px phone box).
      var i = d.vis - (e.clientX - d.lx) / d.w;
      var lo = d.base - 1, hi = d.base + 1;
      if (lo < 0) lo = 0;
      if (hi > n - 1) hi = n - 1;
      if (i < lo) i = lo + (i - lo) / 3;
      else if (i > hi) i = hi + (i - hi) / 3;
      track.style.setProperty("--i", i);
    });

    stage.addEventListener("pointerup", function (e) {
      var d = drag;                                 // id check BEFORE nulling
      if (!d || e.pointerId !== d.id) return;
      drag = null;
      if (!d.lock) return;                          // a tap: nothing was written
      var dx = e.clientX - d.x, span = e.timeStamp - d.t0;
      var v = (e.timeStamp - d.pt) > 120 ? 0 : d.v; // a stale sample is not a flick
      // Fallback for a flick so fast every pointermove coalesced inside the
      // 30ms window. Bounded so a slow crawl cannot borrow it.
      if (!v && span > 0 && span < 250) v = dx / span;
      var dir = 0;
      if (Math.abs(v) > 0.5) dir = v < 0 ? 1 : -1;
      // Formula byte-identical to home.js:292 on purpose. MEASURED on the real
      // boxes: 72px (the cap) at the 599.58px desktop column, 63px at 350px,
      // 50px at the 280px box a 320 viewport produces. All land correctly, so
      // no PDP-specific number was invented.
      else if (Math.abs(dx) > Math.min(d.w * 0.18, 72)) dir = dx < 0 ? 1 : -1;
      // ARM THE SUPPRESSOR ONLY ON A COMMIT — this line is BELOW the dir
      // computation for a reason, and home.js:280 has it ABOVE, which is a
      // real bug there that must not be copied. The suppressor exists to stop
      // a DOUBLE action. If dir is 0 the index did not move, so a following
      // arrow click is a SINGLE action and must be allowed through. Arming on
      // `lock` instead makes every wobble between the 8px LOCK and the 63-72px
      // COMMIT a dead arrow tap: the button presses down under the finger and
      // nothing happens, silently — a 55px-wide failure band on a phone, 64px
      // on an iPad, on a 44px control that sits on the swipe's own midline.
      // That is the owner's original bug class, reintroduced. home.js gets away
      // with it only because it excludes [data-quick-add] from the gesture for
      // exactly this reason; there is no such exclusion here and the arrows
      // are 25% of a 350px box. Residual case, accepted: a large drag that
      // returns to its origin and releases on an arrow will fire that arrow —
      // rare, visible, and undoable, which beats common, silent and invisible.
      if (dir) pdpDragUntil = Date.now() + 700;
      var i = d.base + dir;
      settle(d, i < 0 ? 0 : (i > n - 1 ? n - 1 : i));   // clamp, never wrap
    });

    // pointercancel = scroll takeover, pinch, long-press menu, OS steal.
    // lostpointercapture = the failure that is invisible AND PERMANENT: a
    // locked drag whose pointer vanishes with neither terminal event (tab
    // backgrounded mid-gesture, OS drag session, pen off the digitiser). Left
    // uncovered the track keeps a fractional --i, a suppressed transition and
    // a pinned will-change layer, and the lock guard kills swipe for the
    // session. It always fires when capture ends and always AFTER the terminal
    // events, so on normal paths drag is null and it costs nothing.
    function abort(e) {
      var d = drag;
      if (!d || e.pointerId !== d.id) return;
      drag = null;
      if (d.lock) settle(d, null);
    }
    stage.addEventListener("pointercancel", abort);
    stage.addEventListener("lostpointercapture", abort);

    // Last, and in this order: take ownership of aria-current and aria-hidden
    // from the DOM's own --i (so even a Liquid seeding mistake self-corrects),
    // then prime FORWARD ONLY, then reveal the arrows and reserve touch-action.
    //
    // NOT warm(0): warm() is modular, so at index 0 it also hydrates n-1 —
    // promoting the LAST image of the product to a full-size fetch on every
    // page load, for the shot a visitor is least likely to reach. On an
    // 8-image PDP that is a third full-size rendition in the load window.
    // The wrap target is unreachable without a keypress or a tap, and every
    // one of those routes through go() -> warm(), which hydrates it then.
    // hydrate(shown()+1) is what pulls slide 2 — deliberately, at defer-script
    // time rather than from the preload scanner, so the LCP window contains
    // exactly one full-size image and the timing does not depend on any
    // engine's lazy-loading geometry.
    //
    // Nothing dead is ever on screen, and a JS-off page is byte-for-byte
    // today's behaviour.
    paint(shown());
    hydrate(shown());
    hydrate((shown() + 1) % n);
    stage.classList.add("pdp-is-gallery");
  });

  /* ── Running total — reflects the quantity stepper ──────────────────── */
  document.querySelectorAll("[data-pdp]").forEach(function (pdp) {
    var unit = parseInt(pdp.getAttribute("data-unit"), 10) || 0; // cents
    var stepper = pdp.querySelector("[data-qty]");
    function totals() {
      // desktop total is inside [data-pdp]; the mobile sticky total sits outside it
      var els = document.querySelectorAll("[data-pdp-total]");
      var qty = stepper ? (parseInt(stepper.querySelector("[data-qty-val]").textContent, 10) || 1) : 1;
      var money = "$" + ((unit * qty) / 100).toFixed(2);
      els.forEach(function (el) { el.textContent = money; });
    }
    if (stepper) {
      stepper.addEventListener("qty:change", totals);
    }
  });

  /* ── Tabs — click + roving arrow keys (matches <Tabs>) ──────────────── */
  document.querySelectorAll("[data-pdp-tabs]").forEach(function (wrap) {
    var tabs = Array.prototype.slice.call(wrap.querySelectorAll("[data-tab]"));
    var panels = wrap.querySelectorAll("[data-tab-panel]");
    if (!tabs.length) return;
    function activate(name, focus) {
      tabs.forEach(function (t) {
        var on = t.getAttribute("data-tab") === name;
        t.classList.toggle("td-tabs__tab--active", on);
        t.setAttribute("aria-selected", on ? "true" : "false");
        t.tabIndex = on ? 0 : -1;
        if (on && focus) t.focus();
      });
      panels.forEach(function (pn) { pn.hidden = pn.getAttribute("data-tab-panel") !== name; });
    }
    tabs.forEach(function (t) {
      t.addEventListener("click", function () { activate(t.getAttribute("data-tab")); });
    });
    wrap.querySelector(".td-tabs").addEventListener("keydown", function (e) {
      if (["ArrowLeft", "ArrowRight", "Home", "End"].indexOf(e.key) === -1) return;
      e.preventDefault();
      var idx = tabs.findIndex(function (t) { return t.getAttribute("aria-selected") === "true"; });
      var next = idx;
      if (e.key === "ArrowLeft") next = (idx - 1 + tabs.length) % tabs.length;
      if (e.key === "ArrowRight") next = (idx + 1) % tabs.length;
      if (e.key === "Home") next = 0;
      if (e.key === "End") next = tabs.length - 1;
      activate(tabs[next].getAttribute("data-tab"), true);
    });
  });

  /* ── Accordion — single open, matches <Accordion> ───────────────────── */
  document.querySelectorAll("[data-accordion]").forEach(function (acc) {
    var items = Array.prototype.slice.call(acc.querySelectorAll(".td-accordion__item"));
    items.forEach(function (item) {
      var q = item.querySelector("[data-acc-q]");
      if (!q) return;
      q.addEventListener("click", function () {
        var isOpen = item.classList.contains("td-accordion__item--open");
        items.forEach(function (it) {
          it.classList.remove("td-accordion__item--open");
          var b = it.querySelector("[data-acc-q]");
          if (b) b.setAttribute("aria-expanded", "false");
        });
        if (!isOpen) { item.classList.add("td-accordion__item--open"); q.setAttribute("aria-expanded", "true"); }
      });
    });
  });
})();
